/**
 * useExitIntent — détecte l'intention de quitter la page (desktop).
 *
 * Déclenche quand le curseur sort par le haut de la fenêtre (vers la barre
 * d'onglets / fermeture). Volontairement desktop-only : les pop-ups d'exit
 * sur mobile sont intrusifs et pénalisés (UX + SEO). Sur mobile, la conversion
 * est portée par le sticky CTA et la bannière de reprise.
 *
 * - S'arme après `armDelayMs` (évite les faux positifs au chargement).
 * - Ne se déclenche qu'une fois par session (clé sessionStorage).
 */

import { useEffect, useState } from 'react';

interface Options {
  enabled?: boolean;
  armDelayMs?: number;
  sessionKey?: string;
}

export function useExitIntent({
  enabled = true,
  armDelayMs = 6000,
  sessionKey = 'sef_exit_intent_shown',
}: Options = {}): { triggered: boolean; reset: () => void } {
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    // Desktop uniquement (souris fine, pas tactile)
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (sessionStorage.getItem(sessionKey) === '1') return;

    let armed = false;
    const armTimer = setTimeout(() => {
      armed = true;
    }, armDelayMs);

    const onMouseOut = (e: MouseEvent) => {
      if (!armed) return;
      // Sortie par le haut, sans élément de destination (quitte la fenêtre)
      if (e.relatedTarget === null && e.clientY <= 0) {
        sessionStorage.setItem(sessionKey, '1');
        setTriggered(true);
        document.removeEventListener('mouseout', onMouseOut);
      }
    };

    document.addEventListener('mouseout', onMouseOut);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseout', onMouseOut);
    };
  }, [enabled, armDelayMs, sessionKey]);

  return { triggered, reset: () => setTriggered(false) };
}
